import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import mammoth from 'mammoth';
import { logger } from '../utils/logger';

interface DocxViewerProps {
  /** URL to fetch the docx file from, or ArrayBuffer of the file content */
  source: string | ArrayBuffer;
  /** Optional callback when document is loaded */
  onLoad?: () => void;
  /** Optional callback when error occurs */
  onError?: (error: string) => void;
  /** Whether to use full height */
  fullHeight?: boolean;
}

/**
 * Component to view .docx files using mammoth.js
 * Converts docx to HTML and renders it in the browser
 */
export const DocxViewer: React.FC<DocxViewerProps> = ({
  source,
  onLoad,
  onError,
  fullHeight = false
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadDocx = async () => {
      setLoading(true);
      setError(null);
      setHtmlContent('');

      try {
        let arrayBuffer: ArrayBuffer;

        if (typeof source === 'string') {
          // Source is a URL, fetch it
          logger.log('📄 DocxViewer: Fetching docx from URL:', source);
          const response = await fetch(source, {
            credentials: 'include'
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch document: ${response.status}`);
          }
          
          arrayBuffer = await response.arrayBuffer();
          logger.log('📄 DocxViewer: Fetched docx, size:', arrayBuffer.byteLength, 'bytes');
        } else {
          // Source is already an ArrayBuffer
          arrayBuffer = source;
          logger.log('📄 DocxViewer: Using provided ArrayBuffer, size:', arrayBuffer.byteLength, 'bytes');
        }

        // Convert docx to HTML using mammoth
        logger.log('📄 DocxViewer: Converting docx to HTML...');
        const result = await mammoth.convertToHtml({ arrayBuffer });
        
        if (result.messages && result.messages.length > 0) {
          logger.log('📄 DocxViewer: Conversion messages:', result.messages);
        }

        logger.log('📄 DocxViewer: Conversion successful');
        setHtmlContent(result.value);
        onLoad?.();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Không thể tải tài liệu';
        logger.error('📄 DocxViewer: Error loading docx:', err);
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (source) {
      loadDocx();
    }
  }, [source, onLoad, onError]);

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: fullHeight ? '100%' : 'auto',
          minHeight: fullHeight ? '100%' : '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={40} />
          <Typography>Đang tải tài liệu Word...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          height: fullHeight ? '100%' : 'auto',
          minHeight: fullHeight ? '100%' : '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: fullHeight ? '100%' : 'auto',
        minHeight: fullHeight ? '100%' : '400px',
        overflow: 'auto',
        backgroundColor: '#ffffff',
        p: 3,
        // Style the rendered HTML content
        '& .docx-content': {
          maxWidth: '800px',
          margin: '0 auto',
          fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: '14px',
          lineHeight: 1.6,
          color: '#333',
          // Table styles
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: '1em',
            '& td, & th': {
              border: '1px solid #ddd',
              padding: '8px',
              textAlign: 'left'
            },
            '& th': {
              backgroundColor: '#f5f5f5',
              fontWeight: 600
            }
          },
          // Image styles
          '& img': {
            maxWidth: '100%',
            height: 'auto'
          },
          // List styles
          '& ul, & ol': {
            paddingLeft: '2em',
            marginBottom: '1em'
          },
          // Heading styles
          '& h1': {
            fontSize: '2em',
            fontWeight: 600,
            marginTop: '0.5em',
            marginBottom: '0.5em',
            color: '#1a1a1a'
          },
          '& h2': {
            fontSize: '1.5em',
            fontWeight: 600,
            marginTop: '0.5em',
            marginBottom: '0.5em',
            color: '#1a1a1a'
          },
          '& h3': {
            fontSize: '1.25em',
            fontWeight: 600,
            marginTop: '0.5em',
            marginBottom: '0.5em',
            color: '#1a1a1a'
          },
          '& h4, & h5, & h6': {
            fontSize: '1em',
            fontWeight: 600,
            marginTop: '0.5em',
            marginBottom: '0.5em'
          },
          // Paragraph styles
          '& p': {
            marginBottom: '1em'
          },
          // Link styles
          '& a': {
            color: '#1976d2',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }
        }
      }}
    >
      <div 
        className="docx-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }} 
      />
    </Box>
  );
};

export default DocxViewer;
